import * as React from 'react';

import { cn } from '../../lib/cn';

// The status line and its dot share a tone, so a failed entry reads red top to bottom.
const STATUS_TONE = {
  warning: 'text-dusq-warning',
  negative: 'text-dusq-negative',
};
const DOT_TONE = {
  warning: 'bg-dusq-warning',
  negative: 'bg-dusq-negative',
};

// The design's dotted rail. Figma exports it as a fixed 98px image sized for two
// entries, so it is drawn in CSS and stretches to however many the API returns.
// An item with `state: 'pending'` is a stage not yet filed: hollow dot, muted title.
export const Timeline = React.forwardRef(({ className, items = [], ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-col gap-2.5', className)} {...props}>
    {items.map((item, index) => {
      const pending = item.state === 'pending';
      return (
        <div key={item.id} className="flex gap-[11px]">
          <div className="relative w-[10px] shrink-0">
            <span
              className={cn(
                'absolute left-0 top-0 h-[10px] w-[10px] rounded-full',
                pending
                  ? 'border-2 border-dusq-grey600 bg-transparent'
                  : DOT_TONE[item.tone] || 'bg-dusq-positive-value'
              )}
            />
            {/* -bottom-2.5 spans the flex gap, meeting the next dot exactly. */}
            {index < items.length - 1 && (
              <span className="absolute -bottom-2.5 left-1/2 top-[10px] w-0.5 -translate-x-1/2 bg-dusq-timeline-rail" />
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <span className="font-dusq text-[11px] text-dusq-text-history">{item.date}</span>
            <div className="flex flex-col gap-1">
              <span
                className={cn(
                  'font-dusq text-[14px]',
                  pending ? 'font-medium text-dusq-grey400' : 'font-bold text-dusq-text'
                )}
              >
                {item.title}
              </span>
              {item.summary && (
                <span className="font-dusq text-[12.5px] leading-[1.4] text-dusq-text-history">
                  {item.summary}
                </span>
              )}
              {item.status && (
                <span
                  className={cn(
                    'font-dusq text-[12.5px] font-bold',
                    STATUS_TONE[item.tone] || 'text-dusq-positive-value'
                  )}
                >
                  {item.status}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
));
Timeline.displayName = 'Timeline';
